import appConfig from '../config/app-config';
import type { CcirDetailsResponse } from '../components/pages/credit-analysis/types';

const { address: API_URL, key: API_KEY } = appConfig.apis.creditAnalysisApiEnv;

const headers = (): Record<string, string> => {
    const h: Record<string, string> = { 'Content-Type': 'application/json' };
    if (API_KEY) h['x-api-key'] = API_KEY;
    return h;
};

/** Error carrying the HTTP status so callers can branch on a structured signal instead of message text. */
export class ApiError extends Error {
    readonly status: number;
    constructor(status: number, message: string) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        // Required for `instanceof` to work when compiling to ES5 (see tsconfig target).
        Object.setPrototypeOf(this, new.target.prototype);
    }
}

/** Build an ApiError from a failed response, preferring the backend's error/message copy. */
const apiErrorFromResponse = async (res: Response): Promise<ApiError> => {
    const body = await res.json().catch(() => ({}));
    const message = body?.errors?.[0]?.message || body?.message || `HTTP ${res.status}`;
    return new ApiError(res.status, message);
};

export interface CreateBatchRequest {
    batchName: string;
    assignor: { uuid: string; name: string; document: string };
    items: {
        document: string;
        draweeName: string;
        requestedAmount: number;
        dueDate?: string;
        observation?: string;
    }[];
}

export interface CreateBatchResponse {
    id: string;
    batchNumber: string;
    batchName: string;
    status: string;
    totalItems: number;
    gatheredCount: number;
    errorCount: number;
    analysisDispatched: boolean;
}

export interface BatchListItem {
    id: string;
    batchNumber: string;
    batchName: string;
    status: string;
    assignorName: string;
    totalItems: number;
    gatheredCount: number;
    errorCount: number;
    approvedCount: number;
    rejectedCount: number;
    pendingCount: number;
    createdAt: string;
}

export interface BatchDetail {
    id: string;
    batchNumber: string;
    batchName: string;
    status: string;
    assignorName: string;
    assignorDocument: string;
    createdAt: string;
    items: BatchDetailItem[];
}

export interface BatchDetailItem {
    id: string;
    document: string;
    draweeName: string;
    requestedAmount: number;
    status: string;
    observation?: string;
    companyCity?: string;
    companyState?: string;
    companyRoot?: string;
    companyPersonType?: string;
    companyFoundingYear?: string;
    companyQsa?: number;
    exposureScrTotal?: number;
    exposureScrRural?: number;
    exposureOverdue?: number;
    exposureCurrent?: number;
    exposurePotential?: number;
    financialAnnualRevenue?: number;
    financialEquity?: number;
    financialApprovedCreditLimit?: number;
    financialRating?: string;
    pafDate?: string;
    previousAnalysis: boolean;
    bureauStatuses?: Record<string, string>;
    bureauErrors?: Record<string, string>;
    errorMessage?: string;
}

export async function createBatchProcessing(request: CreateBatchRequest): Promise<CreateBatchResponse> {
    const response = await fetch(`${API_URL}/batch-processing`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify(request),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(error.error || `HTTP ${response.status}`);
    }
    return response.json();
}

export async function listBatchProcessing(): Promise<{ total: number; items: BatchListItem[] }> {
    const response = await fetch(`${API_URL}/batch-processing`, { headers: headers() });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
}

export async function getBatchDetail(batchId: string): Promise<BatchDetail> {
    const response = await fetch(`${API_URL}/batch-processing/${batchId}`, { headers: headers() });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
}


export async function refreshPaf(batchId: string, itemIds: string[]): Promise<{ updated: number }> {
    const response = await fetch(`${API_URL}/batch-processing/${batchId}/refresh-paf`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ itemIds }),
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
}

export async function saveDecision(
    batchId: string,
    itemId: string,
    decision: { status: string; approvedValue?: number; justification?: string; creditObs?: string; cprNumber?: string; decidedBy?: string },
): Promise<{ updated: number }> {
    const response = await fetch(`${API_URL}/analysis-items/${itemId}/decision`, {
        method: 'PUT',
        headers: headers(),
        body: JSON.stringify({ batchId, ...decision }),
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
}

export async function saveBatchDecision(
    batchId: string,
    items: { itemId: string; approvedValue?: string }[],
    decision: { status: string; justification?: string; creditObs?: string; decidedBy?: string },
): Promise<{ updated: number }> {
    const response = await fetch(`${API_URL}/analysis-items/batch-decision`, {
        method: 'PUT',
        headers: headers(),
        body: JSON.stringify({ batchId, items, ...decision }),
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
}

export async function getFeatureFlags(): Promise<Record<string, boolean>> {
    const response = await fetch(`${API_URL}/feature-flags`, { headers: headers() });
    if (!response.ok) return {};
    return response.json();
}


export async function reprocessBatch(batchId: string): Promise<{ regathered: number; status: string; dispatched: boolean }> {
    const response = await fetch(`${API_URL}/batch-processing/${batchId}/reprocess`, {
        method: 'POST',
        headers: headers(),
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
}

export async function continueAnalysis(batchId: string, itemIds: string[], bureauCode?: string): Promise<{ subBatchId: string; itemCount: number; pipeline: string[] }> {
    const body: any = { itemIds };
    if (bureauCode) body.bureauCode = bureauCode;
    const response = await fetch(`${API_URL}/batch-processing/${batchId}/continue-analysis`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
}

export async function getColumnTemplates(): Promise<Record<string, Record<number, string>>> {
    const response = await fetch(`${API_URL}/column-templates`, { headers: headers() });
    if (!response.ok) return {};
    return response.json();
}

export async function saveColumnTemplate(name: string, columnTags: Record<number, string>): Promise<void> {
    const res = await fetch(`${API_URL}/column-templates`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ name, columnTags }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

export async function deleteColumnTemplate(name: string): Promise<void> {
    const res = await fetch(`${API_URL}/column-templates/${encodeURIComponent(name)}`, {
        method: 'DELETE',
        headers: headers(),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

export interface PafAnalysisData {
    document: string;
    generalData: {
        name: string;
        tradeName: string | null;
        city: string | null;
        state: string | null;
        rating: string | null;
        creditOpinion: string | null;
        economicGroup: string | null;
        approvedCreditLimit: number | null;
        netRevenue: number | null;
    };
    periods: { closingDate: string | null; data: Record<string, string> }[];
    references: { date: string | null; name: string; source: string; comment: string }[];
    exposure: number | null;
}

export async function getPafAnalysis(batchId: string, itemId: string): Promise<PafAnalysisData> {
    const res = await fetch(`${API_URL}/batch-processing/${batchId}/items/${itemId}/paf-analysis`, { headers: headers() });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
}

export interface BureauDetail {
    bureau_name?: string;
    bureau?: string;
    status?: string;
    data?: Record<string, unknown>;
}

export interface BureauDetailsResponse {
    bureaus: BureauDetail[];
}

export async function getBureauDetails(itemId: string): Promise<BureauDetailsResponse> {
    const res = await fetch(`${API_URL}/analysis-items/${itemId}/bureau-details`, { headers: headers() });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
}

export async function getCcirDetails(itemId: string): Promise<CcirDetailsResponse> {
    const res = await fetch(`${API_URL}/analysis-items/${itemId}/ccir-details`, { headers: headers() });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
}

export interface DeepAnalysisResult {
    deepAnalysisBatchId: string;
    coOwnerCount: number;
    pipeline: string[];
}

export async function requestDeepAnalysis(itemId: string): Promise<DeepAnalysisResult> {
    const res = await fetch(`${API_URL}/analysis-items/${itemId}/ccir-deep-analysis`, {
        method: 'POST',
        headers: headers(),
    });
    if (res.status === 409) {
        const body = await res.json();
        throw new Error(body.errors?.[0]?.message || 'Análise já solicitada');
    }
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const body = await res.json();
    return body.data;
}

export interface RetryBureauResult {
    retriedItemCount: number;
    bureau: string;
    batchId: string;
}

export async function retryCcirBureau(batchId: string, bureau: string, itemIds?: string[]): Promise<RetryBureauResult> {
    const payload: Record<string, unknown> = { bureau };
    if (itemIds) payload.itemIds = itemIds;
    const res = await fetch(`${API_URL}/ccir-deep-analysis/${batchId}/retry-bureau`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const body = await res.json();
    return body.data;
}

// ── AI Analysis ──

export async function requestAiAnalysis(batchId: string): Promise<{ data: import('../components/pages/credit-analysis/types').AiAnalysisDispatchResponse }> {
    const res = await fetch(`${API_URL}/ai-analysis/${batchId}`, {
        method: 'POST',
        headers: headers(),
    });
    if (res.status === 409) {
        const body = await res.json().catch(() => ({}));
        throw new ApiError(409, body.errors?.[0]?.message || 'Análise de IA já foi solicitada para este lote');
    }
    if (res.status === 422) {
        const body = await res.json().catch(() => ({}));
        throw new ApiError(422, body.errors?.[0]?.message || 'Nenhum item elegível para análise');
    }
    if (!res.ok) throw await apiErrorFromResponse(res);
    return res.json();
}

export async function getAiAnalysisResults(batchId: string): Promise<import('../components/pages/credit-analysis/types').AiAnalysisResponse> {
    const res = await fetch(`${API_URL}/ai-analysis/${batchId}`, { headers: headers() });
    if (!res.ok) throw await apiErrorFromResponse(res);
    const body = await res.json();
    return body.data;
}

export interface AnalysisItemsParams {
    page?: number;
    size?: number;
    batchId?: string;
    batchNumber?: string;
    assignor?: string;
    drawee?: string;
    document?: string;
}

export interface AnalysisItemsResponse {
    content: unknown[];
    totalElements: number;
}

export async function listAnalysisItems(params: AnalysisItemsParams): Promise<AnalysisItemsResponse> {
    const searchParams = new URLSearchParams();
    if (params.page !== undefined) searchParams.set('page', String(params.page));
    if (params.size !== undefined) searchParams.set('size', String(params.size));
    if (params.batchId) searchParams.set('batchId', params.batchId);
    if (params.batchNumber) searchParams.set('batchNumber', params.batchNumber);
    if (params.assignor) searchParams.set('assignor', params.assignor);
    if (params.drawee) searchParams.set('drawee', params.drawee);
    if (params.document) searchParams.set('document', params.document);

    const res = await fetch(`${API_URL}/analysis-items?${searchParams.toString()}`, { headers: headers() });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
}
